import { atlasPathOf, atlasUrls } from '@common/helpers/atlasUrls'
import {
  ArcActivityOutcome,
  ArcActivityPhase,
  IArcActivity,
  IArcActivityRef,
} from '@common/interfaces/arcPresence'
import { ApprovalStatus } from '@common/interfaces/atlasai'

/** Bursts are spaced this far apart, so five rows landing at once read as five, not a flicker. */
export const STAGGER_MS = 400
/** A step stays "working" at least this long, so a fast write is still seen being made. */
export const MIN_WORKING_MS = 700
/** How long a finished write keeps its glow, long enough for a refetched row to land and catch it. */
export const DONE_LINGER_MS = 6000
/** A prepared change waits on its approval card this long, unless it is resolved first. */
export const APPROVAL_LINGER_MS = 60 * 1000
const ELSEWHERE_LINGER_MS = 8000
const ELSEWHERE_MAX = 3
const READ_ONLY = 'READ_ONLY'

/** A change Arc finished making: the end of a write that succeeded. */
export const isDoneWrite = (activity: IArcActivity): boolean =>
  activity.phase === ArcActivityPhase.End &&
  activity.outcome === ArcActivityOutcome.Done &&
  activity.tier !== READ_ONLY

/** The service lists show placeholders for while Arc makes a task. */
export const ARC_CREATE_TASK_SERVICE = 'CreateAtlasTask'

export enum ArcPresenceState {
  Working = 'working',
  Done = 'done',
  NeedsApproval = 'needs_approval',
  Failed = 'failed',
}

export interface IArcPresenceEntry {
  id: string
  /** The Arc turn doing it, so a call can tell its own work from a chat's. */
  chatHistoryId: string
  service: string
  isWrite: boolean
  label: string | null
  refs: IArcActivityRef[]
  state: ArcPresenceState
  approvalId: string | null
}

/** A change Arc made where nobody is looking: offered as a chip with the way there. */
export interface IArcElsewhereNote {
  id: string
  text: string
  link: string | null
}

/** A page Arc asked to take the person to, waiting for the router to go there. */
export interface IArcNavigationRequest {
  id: number
  url: string
  label: string | null
}

/** An approval card answered: APPROVAL_RESOLVED. */
export interface IArcApprovalResolution {
  approvalId: string
  status: ApprovalStatus
  /** The turn that asked for the approval. */
  atlasChatHistoryId?: string | null
  /** The turn Arc resumed on to carry on after it, if one started. */
  continuationChatHistoryId?: string | null
}

/** What one Arc turn did on screen: changed something, or moved them to a page. */
export interface IArcTurnEffects {
  wrote: boolean
  navigated: boolean
}

/** Turns remembered; a call only asks about its own recent ones. */
const TURN_EFFECTS_MAX = 200

/** An Arc reply posted into a conversation: what a call needs from CHANNEL_MESSAGE_CREATED. */
export interface IArcPostedReply {
  channelId: string
  arcTurnId: string | null
  voiceCallMessageId?: string | null
  content: string
}

export interface IArcPresenceSnapshot {
  entries: IArcPresenceEntry[]
  elsewhere: IArcElsewhereNote[]
  navigation: IArcNavigationRequest | null
}

const STATE_FOR_OUTCOME: Record<ArcActivityOutcome, ArcPresenceState> = {
  [ArcActivityOutcome.Done]: ArcPresenceState.Done,
  [ArcActivityOutcome.NeedsApproval]: ArcPresenceState.NeedsApproval,
  [ArcActivityOutcome.Failed]: ArcPresenceState.Failed,
}

interface IVerb {
  working: string
  done: string
}

const VERBS: Record<string, IVerb> = {
  Create: { working: 'is creating', done: 'created' },
  Add: { working: 'is adding', done: 'added' },
  Update: { working: 'is updating', done: 'updated' },
  Move: { working: 'is moving', done: 'moved' },
  Delete: { working: 'is deleting', done: 'deleted' },
  Remove: { working: 'is removing', done: 'removed' },
  Set: { working: 'is updating', done: 'updated' },
  Reorder: { working: 'is reordering', done: 'reordered' },
}

const ANY_CHANGE: IVerb = { working: 'is making', done: 'made' }

/** Services registered under a bare verb: the notes domain's `Add` / `Update` / `Delete`. */
const BARE_VERB_NOUN = 'note'

/**
 * `CreateAtlasTaskComment` + "Call notes" → `Arc created a task comment: “Call notes”`,
 * or `Arc is creating a task comment: “Call notes”` while it is still working.
 */
export const describeWrite = (service: string, label: string | null, isDone = true): string => {
  const [verb, ...rest] = service.split(/(?=[A-Z])/)
  const noun =
    rest
      .filter((word) => word !== 'Atlas')
      .join(' ')
      .toLowerCase() || BARE_VERB_NOUN
  const tense = isDone ? 'done' : 'working'
  const action = VERBS[verb] ? `${VERBS[verb][tense]} a ${noun}` : `${ANY_CHANGE[tense]} a change`
  return label ? `Arc ${action}: “${label}”` : `Arc ${action}`
}

/** Where a chip takes you: the project, task or client the change names, most specific first. */
export const linkFor = (service: string, refs: IArcActivityRef[]): string | null => {
  const refOf = (key: string) => refs.find((ref) => ref.key === key)?.value
  // On a project service a bare `id` is the project; on a section service it is the section.
  const projectId = refOf('projectId') ?? (/TaskProject$/.test(service) ? refOf('id') : undefined)
  if (projectId) return atlasUrls.taskProject(projectId)
  const taskId = refOf('taskId') ?? (/Task(Comment)?$/.test(service) ? refOf('id') : undefined)
  if (taskId) return atlasUrls.task(taskId)
  const clientId = refOf('clientId')
  return clientId ? atlasUrls.client(clientId) : null
}

interface IEntryTimes {
  revealAt: number
  timers: Array<ReturnType<typeof setTimeout>>
}

/**
 * What Arc is doing on screen right now, fed by ARC_ACTIVITY. A row asks it
 * whether Arc is touching that record; a list asks it which tasks Arc is still
 * making for it. Rows on screen say so (`watch`), which is how a change made
 * where nobody is looking becomes a chip instead.
 */
export class ArcPresenceStore {
  private snapshot: IArcPresenceSnapshot = { entries: [], elsewhere: [], navigation: null }

  private navigationCount = 0

  private readonly listeners = new Set<() => void>()

  private readonly times = new Map<string, IEntryTimes>()

  /** Activity ids that arrived but aren't shown yet, in the order they will be. */
  private readonly queued = new Map<string, IArcPresenceEntry>()

  private readonly watched = new Map<string, number>()

  private readonly effectsByTurn = new Map<string, IArcTurnEffects>()

  private readonly navigationListeners = new Set<(url: string, label: string | null) => void>()

  private readonly replyListeners = new Set<(reply: IArcPostedReply) => void>()

  private readonly approvalListeners = new Set<(resolution: IArcApprovalResolution) => void>()

  private lastRevealAt = 0

  private isNavigationHeld = false

  constructor(private readonly now: () => number = Date.now) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  readonly getSnapshot = (): IArcPresenceSnapshot => this.snapshot

  /**
   * Something on screen shows this record, or this activity as a placeholder;
   * returns the unwatch. Values are what refs carry (a task id, a client name) or
   * an activity id.
   */
  watch(value: string): () => void {
    this.watched.set(value, (this.watched.get(value) ?? 0) + 1)
    this.touchIfMentioned(value)
    return () => {
      const count = (this.watched.get(value) ?? 1) - 1
      if (count > 0) this.watched.set(value, count)
      else this.watched.delete(value)
      this.touchIfMentioned(value)
    }
  }

  /** A row arriving for a record Arc just made retires its placeholder, so readers must re-read. */
  private touchIfMentioned(value: string): void {
    const isMentioned = this.snapshot.entries.some(
      (entry) => entry.id === value || entry.refs.some((ref) => ref.value === value),
    )
    if (!isMentioned) return
    this.snapshot = { ...this.snapshot }
    this.emit()
  }

  isWatched(value: string): boolean {
    return this.watched.has(value)
  }

  /** A row or placeholder on screen already shows this activity's record. */
  isOnScreen({ id, refs }: { id: string; refs: IArcActivityRef[] }): boolean {
    return this.watched.has(id) || refs.some(({ value }) => this.watched.has(value))
  }

  receive(activity: IArcActivity): void {
    if (activity.phase === ArcActivityPhase.Start) this.start(activity)
    else this.finish(activity)
    if (isDoneWrite(activity)) this.noteTurnEffect(activity.chatHistoryId, 'wrote')
  }

  /** What a turn has done that the person could see, so far. */
  turnEffects(chatHistoryId: string): IArcTurnEffects {
    return this.effectsByTurn.get(chatHistoryId) ?? { wrote: false, navigated: false }
  }

  private noteTurnEffect(chatHistoryId: string | null, effect: keyof IArcTurnEffects): void {
    if (!chatHistoryId) return
    const effects = { ...this.turnEffects(chatHistoryId), [effect]: true }
    this.effectsByTurn.delete(chatHistoryId)
    this.effectsByTurn.set(chatHistoryId, effects)
    const oldest = this.effectsByTurn.keys().next().value
    if (this.effectsByTurn.size > TURN_EFFECTS_MAX && oldest) this.effectsByTurn.delete(oldest)
  }

  /** Its card was approved or rejected: what was waiting on it is done waiting. */
  resolveApproval(resolution: IArcApprovalResolution): void {
    const entry = this.snapshot.entries.find((item) => item.approvalId === resolution.approvalId)
    if (entry) this.remove(entry.id)
    this.approvalListeners.forEach((listener) => listener(resolution))
  }

  /** An Arc reply landed in a conversation. */
  receiveArcReply(reply: IArcPostedReply): void {
    this.replyListeners.forEach((listener) => listener(reply))
  }

  onArcReply(listener: (reply: IArcPostedReply) => void): () => void {
    this.replyListeners.add(listener)
    return () => this.replyListeners.delete(listener)
  }

  /** Hear about every approval answered, e.g. so a call can follow what Arc does next. */
  onApprovalResolved(listener: (resolution: IArcApprovalResolution) => void): () => void {
    this.approvalListeners.add(listener)
    return () => this.approvalListeners.delete(listener)
  }

  /** While held, Arc can't move the screen: mid-call on a phone, a new page would leave the call. */
  holdNavigation(isHeld: boolean): void {
    this.isNavigationHeld = isHeld
  }

  /** Arc asked to open a page. Only an Atlas path is followed; anything else is ignored. */
  requestNavigation(url: string, label: string | null, chatHistoryId: string | null = null): void {
    const path = atlasPathOf(url)
    if (!path || this.isNavigationHeld) return
    this.noteTurnEffect(chatHistoryId, 'navigated')
    this.navigationListeners.forEach((listener) => listener(path, label))
    this.navigationCount += 1
    this.snapshot = { ...this.snapshot, navigation: { id: this.navigationCount, url: path, label } }
    this.emit()
  }

  /** Hear the moment Arc moves someone's screen, before the page has even loaded. */
  onNavigation(listener: (url: string, label: string | null) => void): () => void {
    this.navigationListeners.add(listener)
    return () => this.navigationListeners.delete(listener)
  }

  /** The router went there. */
  completeNavigation(id: number): void {
    if (this.snapshot.navigation?.id !== id) return
    this.snapshot = { ...this.snapshot, navigation: null }
    this.emit()
  }

  dismissElsewhere(id: string): void {
    this.setElsewhere(this.snapshot.elsewhere.filter((note) => note.id !== id))
  }

  private start(activity: IArcActivity): void {
    if (this.times.has(activity.id)) return
    const revealAt = Math.max(this.now(), this.lastRevealAt + STAGGER_MS)
    this.lastRevealAt = revealAt
    const entry: IArcPresenceEntry = {
      id: activity.id,
      chatHistoryId: activity.chatHistoryId,
      service: activity.service,
      isWrite: activity.tier !== READ_ONLY,
      label: activity.label,
      refs: activity.refs,
      state: ArcPresenceState.Working,
      approvalId: null,
    }
    const times: IEntryTimes = { revealAt, timers: [] }
    this.times.set(activity.id, times)
    this.queued.set(activity.id, entry)
    times.timers.push(this.at(revealAt, () => this.reveal(activity.id)))
  }

  private reveal(id: string): void {
    const entry = this.queued.get(id)
    if (!entry) return
    this.queued.delete(id)
    this.setEntries([...this.snapshot.entries, entry])
  }

  private finish(activity: IArcActivity): void {
    // An end whose start never arrived (a reconnect, a reload) is shown from here.
    if (!this.times.has(activity.id)) this.start(activity)
    const times = this.times.get(activity.id)
    if (!times) return
    const settleAt = Math.max(this.now(), times.revealAt + MIN_WORKING_MS)
    times.timers.push(this.at(settleAt, () => this.settle(activity)))
  }

  private settle(activity: IArcActivity): void {
    const state = STATE_FOR_OUTCOME[activity.outcome ?? ArcActivityOutcome.Done]
    const isWrite = activity.tier !== READ_ONLY
    // A look-up is shown while it runs; only a change is worth a glow afterwards.
    if (!isWrite) {
      this.remove(activity.id)
      return
    }
    this.setEntries(
      this.snapshot.entries.map((entry) =>
        entry.id === activity.id
          ? {
              ...entry,
              state,
              label: activity.label ?? entry.label,
              refs: activity.refs,
              approvalId: activity.approvalId,
            }
          : entry,
      ),
    )
    if (state === ArcPresenceState.Done) this.noteIfElsewhere(activity)
    const linger = state === ArcPresenceState.NeedsApproval ? APPROVAL_LINGER_MS : DONE_LINGER_MS
    this.times
      .get(activity.id)
      ?.timers.push(this.at(this.now() + linger, () => this.remove(activity.id)))
  }

  private noteIfElsewhere(activity: IArcActivity): void {
    if (this.isOnScreen(activity)) return
    const note: IArcElsewhereNote = {
      id: activity.id,
      text: describeWrite(activity.service, activity.label),
      link: linkFor(activity.service, activity.refs),
    }
    this.setElsewhere([...this.snapshot.elsewhere, note].slice(-ELSEWHERE_MAX))
    this.at(this.now() + ELSEWHERE_LINGER_MS, () => this.dismissElsewhere(note.id))
  }

  private remove(id: string): void {
    this.times.get(id)?.timers.forEach((timer) => clearTimeout(timer))
    this.times.delete(id)
    this.queued.delete(id)
    if (this.snapshot.entries.some((entry) => entry.id === id)) {
      this.setEntries(this.snapshot.entries.filter((entry) => entry.id !== id))
    }
  }

  private at(time: number, fn: () => void): ReturnType<typeof setTimeout> {
    return setTimeout(fn, Math.max(0, time - this.now()))
  }

  private setEntries(entries: IArcPresenceEntry[]): void {
    this.snapshot = { ...this.snapshot, entries }
    this.emit()
  }

  private setElsewhere(elsewhere: IArcElsewhereNote[]): void {
    this.snapshot = { ...this.snapshot, elsewhere }
    this.emit()
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener())
  }
}

/** The app's one store: ARC_ACTIVITY lands in it through a saga, and rows read it through hooks. */
export const arcPresence = new ArcPresenceStore()
